import { useEffect, useRef, useState } from "react";

// ---- drivers (safe for SSR/private modes) ----
const ls = {
  getItem: (k) =>
    typeof window !== "undefined" && window.localStorage ? localStorage.getItem(k) : null,
  setItem: (k, v) => {
    if (typeof window !== "undefined" && window.localStorage) localStorage.setItem(k, v);
  },
  removeItem: (k) => {
    if (typeof window !== "undefined" && window.localStorage) localStorage.removeItem(k);
  },
};

const ss = {
  getItem: (k) =>
    typeof window !== "undefined" && window.sessionStorage ? sessionStorage.getItem(k) : null,
  setItem: (k, v) => {
    if (typeof window !== "undefined" && window.sessionStorage) sessionStorage.setItem(k, v);
  },
  removeItem: (k) => {
    if (typeof window !== "undefined" && window.sessionStorage) sessionStorage.removeItem(k);
  },
};

// ---- core store with TTL + versioning ----
const keyFor = (ns, k) => (ns ? `${ns}:${k}` : k);
const now = () => Date.now();

class Store {
  constructor(driver, opts = {}) {
    this.driver = driver;
    this.opts = opts;
  }
  get(k) {
    try {
      const raw = this.driver.getItem(keyFor(this.opts.namespace, k));
      if (!raw) return null;
      const { v, e, d } = JSON.parse(raw);
      if (this.opts.version && v !== this.opts.version) {
        this.remove(k);
        return null;
      }
      if (e && e < now()) {
        this.remove(k);
        return null;
      }
      return d;
    } catch {
      this.remove(k);
      return null;
    }
  }
  set(k, value, override) {
    try {
      const ttl = override?.ttlMs != null ? override.ttlMs : this.opts.ttlMs;
      const env = {
        v: override?.version != null ? override.version : this.opts.version,
        e: ttl ? now() + ttl : undefined,
        d: value,
      };
      this.driver.setItem(keyFor(this.opts.namespace, k), JSON.stringify(env));
    } catch (err) {
      console.warn(
        `[Store] Failed to write key "${k}" — ${err?.name}: ${err?.message}. ` +
          "Data may exceed the browser's storage quota."
      );
    }
  }
  remove(k) {
    this.driver.removeItem(keyFor(this.opts.namespace, k));
  }
}

export const appLocal = new Store(ls, { namespace: "daitaflow", version: 1 });
export const appSession = new Store(ss, { namespace: "daitaflow", version: 1 });

// Canonical keys (no more string literals around the codebase)
export const KEYS = {
  /** Single object: { [companyId]: { company?, projects?, status?, error? } } */
  companiesData: "resource:companiesData",
  user: "resource:user",
  cmDraft: "change:management:draft",
  /** Object: { [companyId]: boolean } — which home-page accordions are expanded */
  homeAccordions: "ui:homeAccordions",
};

// ---- SWR-lite hook (cache first; revalidate on focus/online) ----

export function useResource(opts) {
  const {
    cacheGet,
    cacheSet,
    fetcher,
    revalidateOnFocus = false,
    revalidateOnReconnect = false,
  } = opts;

  const [data, setData] = useState(() => cacheGet());
  const [loading, setLoading] = useState(!cacheGet());
  const [error, setError] = useState(null);
  const inflight = useRef(null);

  async function load(force = false, ignoreCache = false) {
    if (inflight.current) return inflight.current;
    if (!force && data) return Promise.resolve(data);
    setLoading(true);
    const p = fetcher(ignoreCache)
      .then((d) => {
        cacheSet(d);
        setData(d);
        setError(null);
        return d;
      })
      .catch((e) => {
        setError(e);
        throw e;
      })
      .finally(() => {
        inflight.current = null;
        setLoading(false);
      });
    inflight.current = p;
    return p;
  }

  useEffect(() => {
    if (!data) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // initial fill

  useEffect(() => {
    const onFocus = () => revalidateOnFocus && void load(true);
    const onOnline = () => revalidateOnReconnect && navigator.onLine && void load(true);
    const onVis = () => document.visibilityState === "visible" && onFocus();

    window.addEventListener("visibilitychange", onVis);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("online", onOnline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revalidateOnFocus, revalidateOnReconnect]);

  return { data, loading, error, refresh: () => load(true, true) };
}

// ---- Single session blob: { [companyId]: { company?, projects?, status?, error? } } ----
// projects per company is stored as { [projectId]: project } for O(1) lookup.
const TTL_6H = 6 * 60 * 60 * 1000;

function projectMapGet(projects, projectId) {
  if (!projects || typeof projects !== "object") return null;
  const pid = Number(projectId);
  if (Number.isNaN(pid)) return null;
  if (Array.isArray(projects)) return projects.find((p) => p && p.id === pid) || null;
  return projects[String(pid)] ?? projects[pid] ?? null;
}

function projectMapValues(projects) {
  if (!projects || typeof projects !== "object") return [];
  if (Array.isArray(projects)) return projects;
  return Object.values(projects);
}

export function getCompaniesData() {
  const data = appSession.get(KEYS.companiesData);
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    if (data != null) appSession.remove(KEYS.companiesData);
    return null;
  }
  return data;
}

/** Only session storage layer should call this. */
export function setCompaniesData(data) {
  appSession.set(KEYS.companiesData, data, { ttlMs: TTL_6H });
}

/** Companies list: all company objects from the blob. */
export function getCompanies() {
  const data = getCompaniesData();
  if (!data || typeof data !== "object") return null;
  return Object.values(data)
    .map((d) => d?.company)
    .filter(Boolean);
}

/**
 * Get project by company id and project id.
 */
export function getProjectById(companyId, projectId) {
  const data = getCompaniesData();
  if (!data || typeof data !== "object") return null;
  const d = data[String(companyId)] ?? data[Number(companyId)];
  return projectMapGet(d?.projects, projectId);
}

export function hasProjectUsersInStorage(companyId, projectId) {
  const p = getProjectById(companyId, projectId);
  return p != null && Array.isArray(p.users);
}

/** Company users (active + inactive merged) for one company, or null if not in storage. */
export function getCompanyUsers(companyId) {
  const data = getCompaniesData();
  if (!data || typeof data !== "object") return null;
  const d = data[String(companyId)] ?? data[Number(companyId)];
  return Array.isArray(d?.users) ? d.users : null;
}

export function hasCompanyUsersInStorage(companyId) {
  return getCompanyUsers(companyId) != null;
}

/** Projects for one company (array). */
export function getProjectsForCompany(companyId) {
  const data = getCompaniesData();
  if (!data || typeof data !== "object") return [];
  const d = data[String(companyId)] ?? data[Number(companyId)];
  return projectMapValues(d?.projects);
}

// User caching disabled: always fetch fresh from backend and hold in-memory only.
export const getUser = () => null;
export const setUser = () => {};
export const clearUser = () => appSession.remove(KEYS.user);

export const getCmdraft = () => appSession.get(KEYS.cmDraft);
export const setCmdraft = (d) => appSession.set(KEYS.cmDraft, d); // no TTL for drafts
export const clearCmdraft = () => appSession.remove(KEYS.cmDraft);
